import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { Zap } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { PlatformGroupHeader } from './platform-group-header'

type HookRow = RouterOutput['analytics']['hookLength']['byBucket'][number]

const BUCKET_ORDER: Record<string, number> = { punchy: 0, standard: 1, long: 2 }

export function HookLengthCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.hookLength.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  const grouped = data?.byBucket ? groupByPlatform(data.byBucket) : new Map()
  const isMultiPlatform = grouped.size > 1

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Zap size={14} className="text-muted-foreground" />
          Hook Length
          <InfoTooltip>
            How the first line of your caption — the "hook" before "...more" — affects engagement. A
            short, punchy hook tends to convert more readers; longer hooks ask the audience to
            commit.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need posts with varied first-line lengths on at least one platform — vary your hook
            style to unlock this view.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className={cn(isMultiPlatform ? 'space-y-4' : 'space-y-3')}>
            {[...grouped.entries()].map(([platform, rows]) => {
              const ordered = [...rows].sort(
                (a, b) => (BUCKET_ORDER[a.id] ?? 99) - (BUCKET_ORDER[b.id] ?? 99),
              )
              return (
                <div key={platform}>
                  {isMultiPlatform && <PlatformGroupHeader platform={platform} />}
                  <Bars rows={ordered} />
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function Bars({ rows }: { rows: HookRow[] }) {
  const max = Math.max(...rows.map((r) => r.avgEngagement))
  return (
    <div className="space-y-3">
      {rows.map((b) => {
        const widthPct = max > 0 ? (b.avgEngagement / max) * 100 : 0
        return (
          <div key={`${b.platform}-${b.id}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <HintLabel hint={b.hint} className="text-foreground font-medium">
                {b.label}
              </HintLabel>
              <span
                className={cn(
                  'font-medium',
                  b.isBaseline
                    ? 'text-muted-foreground'
                    : b.relativeBoost && b.relativeBoost > 0
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-muted-foreground',
                )}>
                {b.isBaseline ? (
                  <HintLabel hint="Lowest-performing hook length on this platform.">
                    baseline
                  </HintLabel>
                ) : b.relativeBoost != null ? (
                  `+${(b.relativeBoost * 100).toFixed(0)}%`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="bg-muted relative h-2 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-20 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}
